import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { Button } from '~/ui/buttons';
import LightButton from '~/editor/buttons/LightButton';
import ExitIcon from '~/assets/button/close.svg';
import AiIcon from '~/assets/button/ai.svg';
import SpeakerIcon from '~/assets/button/narration.svg';
import SlideNarrationIcon from '~/assets/button/slide_narration.svg';
import OpenPageIcon from '~/assets/button/openPage.svg';
import SingleIcon from '~/assets/button/singleMode.svg';
import PreviewIcon from '~/assets/button/previewMode.svg';
import UndoIcon from '~/assets/button/undo.svg';
import RedoIcon from '~/assets/button/redo.svg';
import NarrationPopup from '~/editor/narration/NarrationPopup';
import { useMessageStore } from '~/editor/store/messageStore';
import MicIcon from '~/assets/editor/mic.svg';
import AIPromptPopup from '~/editor/ai-prompt/AIPromptPopup';
import { usePreview } from '~/editor/hooks/usePreview';
import { usePendingPositionChanges } from '~/editor/hooks/usePendingPositionChanges';
import { Tooltip } from '~/ui/tooltip/Tooltip';

export const ExitIconForBtn = styled(ExitIcon)`
  position: relative;
  max-height: 22px !important;
  margin-top: -4px !important;

  svg {
    path {
      fill: #9845fb !important;
    }
  }
`;

export const SingleIconForBtn = styled(SingleIcon)``;
export const PreviewIconForBtn = styled(PreviewIcon)``;

const PreviewButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Wrapper = styled.div`
  width: 100%;
`;

const Space = styled.div`
  flex: 1;
  position: fixed;
  margin-top: 10px;
  margin-left: 10px;

  &.leftSpace {
    background-color: #fff;
    flex: none;
    padding: 8px;
    border-radius: 16px;
    box-shadow: 3px 3px 5px #0000004f;
    z-index: 200;

    button {
      height: 38px;
      width: 38px;
      margin: 0px;
      padding: 10px 0px;
      border-radius: 0px;

      svg {
        display: inline;
        max-height: 20px;
        path {
          fill: #9845fb;
        }
      }
    }
    button.active {
      svg {
        path {
          fill: #fff;
        }
      }
    }

    button.arrow-left {
      svg {
        margin-top: -8px;
      }
    }

    button.arrow-right {
      svg {
        margin-top: -8px;
      }
    }

    button:first-child,
    button:nth-child(3) {
      border-radius: 0;
    }

    button:nth-child(1) {
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }

    button:nth-child(2) {
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }

    button:first-child {
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }

    button:nth-child(3) {
      border-right: 1px solid #e0e0e0;
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }

    button:nth-child(4) {
      margin-left: 10px;
    }

    button:nth-child(4),
    button:last-child {
      border-radius: 0;
    }

    button:nth-child(4) {
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }

    button:last-child {
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }
  }

  &.centerSpace {
    flex: 1;
    left: calc(50% - 180px);
    z-index: 9999;

    .message {
      position: absolute;
      left: 10px;
      padding: 9px;
      z-index: 200;

      border-radius: 16px;
      background: #9738ff;
      width: 370px;
      display: flex;
      align-items: center;
      gap: 10px;

      color: var(--Grey-1, #fff);
      font-family: Raleway;
      font-weight: 700;
      font-size: 16px;
      font-style: normal;
      line-height: 150%;

      text-align: center;

      svg {
        margin-left: 20px;
      }
    }
  }

  &.rightSpace {
    flex: none;
    text-align: right;

    z-index: 200;
    right: 20px;

    background-color: #fff;
    padding: 8px;
    border-radius: 16px;
    box-shadow: 3px 3px 5px #0000004f;

    button {
      height: 38px;
      margin-left: 5px;
      margin-right: 3px;
      border-radius: 10px;
      svg {
        display: inline;
      }
    }

    button.ai {
      width: 38px;
      padding: 0px;
      margin-left: 5px;
      margin-right: 3px;
    }

    button.speaker {
      width: 170px;
      padding: 0px;
      padding-left: 5px;
      margin-left: 5px;
      margin-right: 3px;
      font-family: ralewayBold;
      font-size: 14px;

      svg {
        margin-left: 10px;
      }
    }

    button.speaker.active {
      background-color: #9845fb;
      color: #fff;

      svg {
        path {
          fill: #fff;
        }
      }
    }

    .rBtn {
      font-family: ralewayBold;
      font-size: 14px !important;

      svg {
        margin-left: 10px;
        margin-top: -3px;
      }
    }

    .rBtn.preview {
      padding-left: 20px;
      padding-right: 15px;
    }
  }
`;

interface HeaderProps {
  mode: string;
  setMode: (mode: 'editor' | 'preview') => void;
  exitRedirectUrl: string;
  publishButtonTitle?: string | null;
  publishRedirectUrl?: string | null;
}

export const Header = ({
  mode,
  setMode,
  exitRedirectUrl,
  publishButtonTitle,
  publishRedirectUrl
}: HeaderProps) => {
  const {
    apiService,
    sequence,
    training,
    undo,
    redo,
    history,
    setSequence,
    setTraining,
    forceUpdateSequence,
    setForceUpdateSequence,
    nextSequence,
    prevSequence,
    resetAIModificationStatus,
    hasFeatureFlag,
    canUndo: storeCanUndo,
    canRedo: storeCanRedo,
    setUndoRedoState,
    undoAction,
    redoAction
  } = useEditorStore();

  const [showNarration, setNarration] = useState(false);
  const { showMessage, message } = useMessageStore();
  const [showAIPromptPopup, setAIPromptPopup] = useState(false);
  const { openPreview } = usePreview();
  const pendingPositionChanges = usePendingPositionChanges();
  const [showPreviewTooltip, setShowPreviewTooltip] = useState(false);

  const shouldShowAIButton = useMemo(() => {
    return (
      mode === 'editor' &&
      sequence &&
      [
        'Multimedia4',
        'Quiz2',
        'SocialMedia1',
        'Statement2',
        'Statement5'
      ].includes(sequence.templateName)
    );
  }, [mode, sequence?.templateName]);

  const [isUndoDisabled, setIsUndoDisabled] = useState(false);
  const [isRedoDisabled, setIsRedoDisabled] = useState(false);

  const handleUndo = async () => {
    if (isUndoDisabled) return;

    setIsRedoDisabled(true);
    setIsUndoDisabled(true);
    setForceUpdateSequence(false);

    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo && sequence) {
      try {
        await undoAction(sequence.id, getSequenceInfo);
      } catch (error) {
        console.error('Undo failed:', error);
        setIsUndoDisabled(false);
        setIsRedoDisabled(false);
      }
    } else {
      undo(getSequenceInfo);
    }
  };

  const handleRedo = async () => {
    if (isRedoDisabled) return;

    setIsRedoDisabled(true);
    setIsUndoDisabled(true);
    setForceUpdateSequence(false);

    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo && sequence) {
      try {
        await redoAction(sequence.id, getSequenceInfo);
      } catch (error) {
        console.error('Redo failed:', error);
        setIsUndoDisabled(false);
        setIsRedoDisabled(false);
      }
    } else {
      redo(getSequenceInfo);
    }
  };

  const getSequenceInfo = (sequenceId: number, trainingList: any) => {
    if (apiService) {
      apiService.getSequenceInfo(sequenceId).then(_sequence => {
        setSequence(_sequence.sequence);

        if (
          _sequence.sequenceMetadata &&
          hasFeatureFlag('course_editor_backend_undo_redo')
        ) {
          setUndoRedoState(
            _sequence.sequenceMetadata.canUndo,
            _sequence.sequenceMetadata.canRedo
          );
        }

        const updatedTraining = {
          ...trainingList,
          chapters: trainingList.chapters.map((chapter: any) => ({
            ...chapter,
            chapterSequences: chapter?.chapterSequences?.map(
              (chapterSequence: any) => {
                return chapterSequence.sequence.id === sequenceId
                  ? {
                      ...chapterSequence,
                      sequence: {
                        ...chapterSequence.sequence,
                        ..._sequence.sequence
                      }
                    }
                  : chapterSequence;
              }
            )
          }))
        };
        setTraining(updatedTraining);
        setForceUpdateSequence(true);

        setIsUndoDisabled(false);
        setIsRedoDisabled(false);
      });
    }
  };

  const onPublish = async () => {
    if (apiService && training) {
      await apiService.publishTraining(training.id).then((res: any) => {
        if (publishRedirectUrl) {
          const resultRedirectUrl =
            process.env.REACT_APP_BACKEND_DASHBOARD_BASE_URL +
            publishRedirectUrl;

          if (window.top && window.self !== window.top) {
            window.top.location.href = resultRedirectUrl;
          } else {
            window.location.href = resultRedirectUrl;
          }
          return;
        } else {
          if (res.redirectUrl && window) {
            if (window.top && window.self !== window.top) {
              window.top.location.href = res.redirectUrl;
            } else {
              window.location.href = res.redirectUrl;
            }
          }
        }
      });
    }
  };

  const onExit = () => {
    if (!window || !exitRedirectUrl) {
      return;
    }

    const resultRedirectUrl =
      process.env.REACT_APP_BACKEND_DASHBOARD_BASE_URL + exitRedirectUrl;

    if (window.parent && window.self !== window.parent) {
      window.parent.location.href = resultRedirectUrl;
    } else {
      window.location.href = resultRedirectUrl;
    }
  };

  const canWeDoRedo = () => {
    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo) {
      return storeCanRedo;
    }

    if (forceUpdateSequence) {
      return false;
    }

    const currentHistory = history.filter(
      item => item.sequenceId == sequence.id
    );

    if (currentHistory.length == 0) {
      return false;
    }

    const activeIndex = currentHistory.findIndex(item => item.active === true);
    return activeIndex >= 0 && activeIndex < currentHistory.length - 1;
  };

  const canWeDoUndo = () => {
    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo) {
      return storeCanUndo;
    }

    if (forceUpdateSequence) {
      return false;
    }

    const currentHistory = history.filter(
      item => item.sequenceId == sequence.id
    );

    if (currentHistory.length == 0) {
      return false;
    }

    const activeIndex = currentHistory.findIndex(item => item.active === true);
    return activeIndex > 0;
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isInputElement =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement)?.contentEditable === 'true' ||
        activeElement?.getAttribute('contenteditable') === 'true';

      if (isInputElement) {
        return;
      }

      if (!training || !sequence) return;

      const allSequences = training.chapters
        .map((chapter: any) =>
          chapter.chapterSequences.map(
            (chapterSequence: any) => chapterSequence.sequence
          )
        )
        .flat();

      if (allSequences.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          if (sequence.id === allSequences[allSequences.length - 1].id) {
            return;
          }
          event.preventDefault();
          nextSequence();
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          if (sequence.id === allSequences[0].id) {
            return;
          }
          event.preventDefault();
          prevSequence();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [training, sequence]);

  return (
    <Wrapper>
      <Space className="leftSpace">
        <LightButton onClick={onExit} style={{ marginRight: '10px' }}>
          <ExitIconForBtn />
        </LightButton>

        <LightButton
          className={mode === 'editor' ? 'active' : ''}
          onClick={() => setMode('editor')}
        >
          <SingleIconForBtn />
        </LightButton>
        <LightButton
          className={mode !== 'editor' ? 'active' : ''}
          onClick={() => setMode('preview')}
        >
          <PreviewIconForBtn />
        </LightButton>
        {mode === 'editor' && (
          <LightButton
            className="arrow arrow-left"
            onClick={handleUndo}
            disabled={!canWeDoUndo() || isUndoDisabled}
          >
            <UndoIcon />
          </LightButton>
        )}
        {mode === 'editor' && (
          <LightButton
            className="arrow arrow-right"
            onClick={handleRedo}
            disabled={!canWeDoRedo() || isRedoDisabled}
          >
            <RedoIcon />
          </LightButton>
        )}
      </Space>
      <Space className="centerSpace">
        {showMessage && (
          <div className="message">
            <MicIcon />
            <p>{message}</p>
          </div>
        )}
      </Space>
      <Space className="rightSpace">
        {shouldShowAIButton && (
          <LightButton onClick={() => setAIPromptPopup(true)} className="ai">
            <AiIcon />
          </LightButton>
        )}
        {mode === 'editor' &&
          sequence &&
          sequence.templateName != 'Multimedia2' && (
            <LightButton
              onClick={() => setNarration(!showNarration)}
              className={sequence.narration ? 'speaker active' : 'speaker'}
            >
              Slide Narration
              {sequence.narration ? <SlideNarrationIcon /> : <SpeakerIcon />}
            </LightButton>
          )}
        <PreviewButtonWrapper
          onMouseEnter={() =>
            pendingPositionChanges > 0 && setShowPreviewTooltip(true)
          }
          onMouseLeave={() => setShowPreviewTooltip(false)}
        >
          <LightButton
            className="rBtn preview"
            onClick={() => openPreview()}
            disabled={pendingPositionChanges > 0}
          >
            Preview
            <OpenPageIcon />
          </LightButton>
          {showPreviewTooltip && pendingPositionChanges > 0 && (
            <Tooltip placement="bottom-center-up" top="50px" right="0">
              Saving changes... Please wait.
            </Tooltip>
          )}
        </PreviewButtonWrapper>
        <Button className="rBtn" onClick={onPublish}>
          {publishButtonTitle
            ? publishButtonTitle
            : training.status == 'live'
            ? 'Assign'
            : 'Publish'}
        </Button>
        {showNarration &&
          createPortal(
            <NarrationPopup onClose={() => setNarration(false)} />,
            document.body
          )}
        {showAIPromptPopup &&
          createPortal(
            <AIPromptPopup onClose={() => setAIPromptPopup(false)} />,
            document.body
          )}
      </Space>
    </Wrapper>
  );
};
